/**
 * «Очередь» на экране «Лиды»: сколько лидов в NEW сейчас и как очередь
 * двигалась по часам — пришло (новые, вернулись на перезвон), ушло (в работу,
 * закрыты), звонки по лидам. Спека `docs/specs/2026-09-24-leads-queue-design.md`;
 * числа — те же, что у `prokat-runner leads-queue`.
 */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { LeadLink } from "@/components/LeadLink";
import { daysAgo } from "@/lib/leadPhones";
import { type LeadQueue, loadLeadQueue, type QueueHour } from "@/lib/leadQueue";
import { shortTime } from "@/lib/leads";

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`очередь «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[22px] font-semibold leading-none tabular-nums">{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

/** Сколько минут лид ждёт в очереди — от последнего входа в NEW до «сейчас». */
function waited(since: string, now: string): string {
  const min = Math.max(0, Math.round((Date.parse(now) - Date.parse(since)) / 60000));
  if (min < 60) return `${min} мин`;
  if (min < 48 * 60) return `${Math.floor(min / 60)} ч ${String(min % 60).padStart(2, "0")} мин`;
  return `${Math.floor(min / 1440)} дн`;
}

const COLS: { key: keyof QueueHour; title: string; hint: string }[] = [
  { key: "queue_start", title: "В начале", hint: "в очереди на начало часа" },
  { key: "in_new", title: "+ новые", hint: "созданы и встали в очередь" },
  { key: "in_back", title: "+ вернулись", hint: "робот вернул к дате следующей обработки" },
  { key: "out_work", title: "− в работу", hint: "переведены в «В работе» (обычно после исходящего)" },
  { key: "out_closed", title: "− закрыты", hint: "сделка, отказ, дубль и прочие статусы" },
  { key: "queue_end", title: "В конце", hint: "в очереди на конец часа (сейчас — для текущего)" },
];

function Hours({ r }: { r: LeadQueue }) {
  const max = Math.max(1, ...r.hours.map((h) => Math.max(h.queue_start, h.queue_end)));
  const th = "whitespace-nowrap px-3 py-2 text-right font-medium";
  const td = "px-3 py-1.5 text-right tabular-nums";
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="очередь по часам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">По часам</h2>
        <span className="text-[12px] text-fg-faint">
          очередь восстановлена по истории статусов; звонок «по лиду» — привязанный к лиду в CRM
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Час</th>
              {COLS.map((c) => (
                <th key={c.key} className={th} title={c.hint}>
                  {c.title}
                </th>
              ))}
              <th className="w-[22%] px-3 py-2 text-left font-medium">Очередь</th>
              <th className={th} title="исходящих по лидам / из них разговоров">Исходящих</th>
              <th className={th} title="входящих по лидам / из них разговоров">Входящих</th>
            </tr>
          </thead>
          <tbody>
            {r.hours.map((h) => (
              <tr key={h.label} className={`border-t border-border ${h.current ? "bg-bg-hover" : ""}`} data-annot={`час ${h.label}`}>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                  {h.label === "00–08" ? "00–08" : `${h.label}:00`}
                  {h.current && <span className="ml-1.5 text-[11px] text-accent">идёт</span>}
                </td>
                {COLS.map((c) => (
                  <td key={c.key} className={`${td} ${h[c.key] === 0 ? "text-fg-faint" : ""}`}>
                    {h[c.key] as number}
                  </td>
                ))}
                <td className="px-3 py-1.5">
                  <div className="h-2 rounded-r-[4px] bg-accent" style={{ width: `${(h.queue_end / max) * 100}%` }} />
                </td>
                <td className={td}>
                  {h.out_calls} <span className="text-fg-faint">/ {h.out_talk}</span>
                </td>
                <td className={td}>
                  {h.in_calls} <span className="text-fg-faint">/ {h.in_talk}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Waiting({ r }: { r: LeadQueue }) {
  const q = r.queue_now;
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="в очереди сейчас">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">В очереди сейчас: {q.total}</h2>
        <span className="text-[12px] text-fg-faint">
          дольше ждут — выше; «вернулся» — лид снова встал в очередь после работы с ним
          {q.no_phone > 0 && `; без российского номера не в очереди: ${q.no_phone}`}
        </span>
      </div>
      {q.leads.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Очередь пуста.</div>
      ) : (
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
              <tr>
                {["Лид", "В очереди с", "Ждёт", "Как попал", "Создан", "Источник", "Телефон", "Исходящих", "Последний"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-2 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.leads.map((x) => (
                <tr key={x.lead_id} className="border-t border-border hover:bg-bg-hover" data-annot={`лид ${x.lead_id}`}>
                  <td className="px-4 py-1.5 font-mono tabular-nums">
                    <LeadLink id={x.lead_id} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{shortTime(x.since)}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{waited(x.since, r.now)}</td>
                  <td className="px-4 py-1.5">{x.returned ? "вернулся" : "новый"}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-fg-muted">{shortTime(x.created)}</td>
                  <td className="px-4 py-1.5">{x.source}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{x.phone || "—"}</td>
                  <td className={`px-4 py-1.5 text-right tabular-nums ${x.out === 0 ? "text-warn" : ""}`}>{x.out}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-fg-muted">{shortTime(x.last_out)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Body({ r }: { r: LeadQueue }) {
  const q = r.queue_now;
  const t = r.totals;
  const day = r.is_today ? "сегодня" : `за ${r.day.split("-").reverse().join(".")}`;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Stat
          label="В очереди сейчас"
          value={q.total}
          hint={`создан сегодня ${q.by_age.today}, вчера ${q.by_age.yesterday}, раньше ${q.by_age.older}`}
        />
        <Stat label={`Пришло ${day}`} value={t.in_new + t.in_back} hint={`новых ${t.in_new}, вернулись ${t.in_back}`} />
        <Stat
          label={`Исходящих по лидам ${day}`}
          value={t.out_calls}
          hint={`${t.out_leads} лидов, разговоров ${t.out_talk}`}
        />
        <Stat label={`Входящих по лидам ${day}`} value={t.in_calls} hint={`разговоров ${t.in_talk}`} />
      </div>
      <Hours r={r} />
      <Waiting r={r} />
    </>
  );
}

export function LeadQueueSection() {
  const [params, setParams] = useSearchParams();
  const today = daysAgo(0);
  const day = params.get("qdate") || today;
  const q = useQuery({
    queryKey: ["lead-queue", day],
    queryFn: () => loadLeadQueue(day),
    // Сегодня очередь живая — обновляем раз в минуту, как крон CRM.
    refetchInterval: day === today ? 60_000 : false,
  });
  const setDay = (d: string) => {
    const out = new URLSearchParams(params);
    if (d === today) out.delete("qdate");
    else out.set("qdate", d);
    setParams(out, { replace: true });
  };
  return (
    <section className="flex flex-col gap-3" data-annot="очередь">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[12px] text-fg-faint">
          очередь — лиды в статусе «Не обработана» с российским номером
          {q.data && ` · данные на ${shortTime(q.data.now).slice(6)}`}
        </span>
        <label className="ml-auto flex items-center gap-2 text-[12.5px] text-fg-muted">
          День
          <input
            type="date"
            value={day}
            max={today}
            min={daysAgo(31)}
            onChange={(e) => e.target.value && setDay(e.target.value)}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
          />
        </label>
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить очередь: {String(q.error.message)}</div>
      ) : (
        <Body r={q.data} />
      )}
    </section>
  );
}
